/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  addTestKitApi,
  assignTestKitToDoctorApi,
  assignTestKitToPatientApi,
  downloadAnalysisApi,
  downloadResultApi,
  getTestKitDetailApi,
  getTestsKitListApi,
  getVisualizationDataApi,
  giveConsentApi,
  revokeResultFromPatientApi,
  shareResultToPatientApi,
  updateKitStatusApi,
  updateTestKitNotesApi,
  uploadCsvReportApi,
  uploadPdfReportApi,
} from "@/api/testKitApi";
import {
  AddTestKitPayload,
  AssignTestKitToDoctorPayload,
  AssignTestKitToPatientPayload,
  TestKitFilterStateType,
  UpdateKitStatusPayload,
  UpdateTestKitNotesPayload,
  UploadCsvReportPayload,
  UploadPdfReportPayload,
} from "@/common/types";
import Types from "../types";
import { setLoader } from "@/redux/actions/commonActions";

export const getTestsKits =
  (page: number = 1, filterState: TestKitFilterStateType, pageSize?: number) =>
  async (dispatch: any) => {
    dispatch(setLoader(true));
    try {
      const result = await getTestsKitListApi(
        page,
        filterState.doctor,
        filterState.patient,
        filterState.status,
        pageSize
      );
      if (result) {
        dispatch({
          type: Types.SET_TEST_KITS,
          payload: result.data,
        });
      }
    } catch (error) {
      console.log(error);
    }
    dispatch(setLoader(false));
  };

export const addTestKit =
  (payload: AddTestKitPayload, callback: (message: string) => void) =>
  async (dispatch: any) => {
    dispatch(setLoader(true));
    try {
      const result = await addTestKitApi(payload);
      if (result) {
        dispatch(getTestsKits(1, { doctor: "", patient: "", status: "" }));
      }
      callback(result.data.status);
    } catch (error) {
      console.log(error);
    }
    dispatch(setLoader(false));
  };

export const getTestKitDetail = (id: number) => async (dispatch: any) => {
  dispatch(setLoader(true));
  const result = await getTestKitDetailApi(id);
  if (result) {
    dispatch({
      type: Types.SET_TEST_KIT_DETAIL,
      payload: result.data,
    });
  }
  dispatch(setLoader(false));
};

export const assignTestKitToDoctor =
  (payload: AssignTestKitToDoctorPayload, callback: () => void) =>
  async (dispatch: any) => {
    dispatch(setLoader(true));
    try {
      const result = await assignTestKitToDoctorApi(payload);
      if (result) {
        dispatch(getTestKitDetail(Number(payload.kit_ids[0])));
      }
      callback();
    } catch (error) {
      console.log(error);
    }
    dispatch(setLoader(false));
  };

export const assignTestKitToPatient =
  (id: number, payload: AssignTestKitToPatientPayload, callback: () => void) =>
  async (dispatch: any) => {
    dispatch(setLoader(true));
    try {
      const result = await assignTestKitToPatientApi(id, payload);
      if (result) {
        dispatch(getTestKitDetail(id));
      }
      callback();
    } catch (error) {
      console.log(error);
    }
    dispatch(setLoader(false));
  };

export const updateTestKitNotes =
  (id: number, payload: UpdateTestKitNotesPayload, callback: () => void) =>
  async (dispatch: any) => {
    dispatch(setLoader(true));
    try {
      const result = await updateTestKitNotesApi(id, payload);
      if (result) {
        dispatch(getTestKitDetail(id));
      }
      callback();
    } catch (error) {
      console.log(error);
    }
    dispatch(setLoader(false));
  };

export const shareResultToPatient =
  (id: number, callback: () => void, errorCallback: () => void) =>
  async (dispatch: any) => {
    try {
      const result = await shareResultToPatientApi(id);
      if (result) {
        dispatch(getTestKitDetail(id));
      }
      callback();
    } catch (error) {
      errorCallback();
      console.log(error);
    }
  };

export const updateKitStatus =
  (id: number, payload: UpdateKitStatusPayload, callback: () => void) =>
  async (dispatch: any) => {
    dispatch(setLoader(true));
    try {
      const result = await updateKitStatusApi(id, payload);
      if (result) {
        dispatch(getTestKitDetail(id));
      }
      callback();
    } catch (error) {
      console.log(error);
    }
    dispatch(setLoader(false));
  };

export const uploadCsvReport =
  (id: number, payload: UploadCsvReportPayload, callback: () => void) =>
  async (dispatch: any) => {
    dispatch(setLoader(true));
    try {
      const result = await uploadCsvReportApi(id, payload);
      if (result) {
        dispatch(getTestKitDetail(id));
      }
      callback();
    } catch (error) {
      console.log(error);
    }
    dispatch(setLoader(false));
  };

export const uploadPdfReport =
  (id: number, payload: UploadPdfReportPayload, callback: () => void) =>
  async (dispatch: any) => {
    dispatch(setLoader(true));
    try {
      const result = await uploadPdfReportApi(id, payload);
      if (result) {
        dispatch(getTestKitDetail(id));
      }
      callback();
    } catch (error) {
      console.log(error);
    }
    dispatch(setLoader(false));
  };

export const downloadAnalysis = (id: number) => async () => {
  try {
    const result = await downloadAnalysisApi(id);
    if (result) {
      const analysisLinks = result.data;
      [analysisLinks.taxa_url, analysisLinks.metrics_url].forEach((url) => {
        if (url) {
          const link = document.createElement("a");
          link.href = url;
          link.target = "_blank";
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }
      });
    }
  } catch (error) {
    console.log(error);
  }
};

export const downloadResult = (id: number) => async () => {
  try {
    const result = await downloadResultApi(id);
    if (result) {
      const resultsUrl = result.data;
      if (resultsUrl.results_url) {
        const link = document.createElement("a");
        link.href = resultsUrl.results_url;
        link.target = "_blank";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    }
  } catch (error) {
    console.log(error);
  }
};

export const getVisualizationData =
  (testId: number) => async (dispatch: any) => {
    try {
      const result = await getVisualizationDataApi(testId);
      if (result) {
        dispatch({
          type: Types.SET_TEST_KIT_VISUALIZATIONS,
          payload: result.data,
        });
      }
    } catch (error) {
      console.log(error);
    }
  };

export const giveConsent = (
  testId: number,
  callback: (message: string) => void
) => {
  return async (dispatch: any) => {
    try {
      dispatch(setLoader(true));
      const result = await giveConsentApi(testId);
      if (result) {
        dispatch(getTestKitDetail(testId));
        callback(result.data.status);
      }
      dispatch(setLoader(false));
    } catch (error) {
      console.log(error);
    }
  };
};

export const revokeResultFromPatient =
  (id: number, callback: () => void, errorCallback: () => void) =>
  async (dispatch: any) => {
    try {
      const result = await revokeResultFromPatientApi(id);
      if (result) {
        dispatch(getTestKitDetail(id));
      }
      callback();
    } catch (error) {
      errorCallback();
      console.log(error);
    }
  };
